<template>
  <div class="detail">
    <div class="detail-title d-flex justify-content-between mb-20 align-items-center">
      <div class="fw-medium">{{ $t("supplier.products") }}</div>

      <div>
        <v-button class="btn-rates p-4" variant="icon" @click="openOffCanvas('the-supplier-details-rates-off-canvas')">
          <v-icon icon="euro" />
        </v-button>

        <v-button class="btn-edit p-4" variant="icon" @click="openModal('the-supplier-resources-create-modal')">
          <v-icon icon="circle-plus" />
        </v-button>
      </div>
    </div>
    <div class="detail-content">
      <div v-if="!visibleResources.length">
        {{ $t("supplier.noProducts") }}
      </div>
      <draggable
        :element="'div'"
        :list="visibleResources"
        class="detail-content resources-container"
        draggable=".item-container:not(.not-draggable)"
        item-key="id"
        @change="onChanged"
      >
        <template #item="{ element }">
          <div class="item-container supplier border rounded-xs p-12 mb-8">
            <div class="align-items-center">
              <div class="d-flex justify-content-between gap-16 align-items-start">
                <div class="flex-grow-1">
                  <div class="fw-medium">{{ element.name }}</div>
                </div>
                <div class="d-flex align-items-center gap-8">
                  <v-button class="shadow-none p-4" variant="icon" @click="showSupplierResourceDetail(element.id)">
                    <v-icon icon="eye" />
                  </v-button>
                  <v-button class="shadow-none p-4" variant="icon" @click="hideSupplierResource(element)">
                    <v-icon icon="archive" />
                  </v-button>
                  <v-button class="shadow-none p-4" variant="icon" @click="deleteSupplierResource(element)">
                    <v-icon icon="trash" />
                  </v-button>
                </div>
              </div>
              <div v-if="!!element.description" class="resource-description text-sm fw-light text-gray-500">
                <span v-html="element.description" />
              </div>
              <span
                v-if="isHotel && element.resourceType"
                class="room-type-badge"
              >
                {{ formatRoomType(element.resourceType) }}
              </span>
            </div>
          </div>
        </template>
      </draggable>

      <div class="d-flex justify-content-center mt-16">
        <v-button
          size="sm"
          variant="outline-primary"
          @click="toggleHidden"
        >
          {{ showHidden ? $t('supplier.hideHiddenProducts') : $t('supplier.showHiddenProducts') }}
        </v-button>
      </div>

      <div v-if="showHidden" class="mt-16">
        <div v-if="!hiddenResources.length" class="text-sm text-gray-500 text-center">
          {{ $t('supplier.noHiddenProducts') }}
        </div>

        <div
          v-for="resource in hiddenResources"
          :key="resource.id"
          class="item-container supplier border rounded-xs p-12 mb-8 opacity-50"
        >
          <div class="align-items-center">
            <div class="d-flex justify-content-between gap-16 align-items-start">
              <div class="flex-grow-1">
                <div class="fw-medium">{{ resource.name }}</div>
              </div>
              <div class="d-flex align-items-center gap-8">
                <v-button
                  class="shadow-none p-4"
                  variant="icon"
                  @click="unhideSupplierResource(resource)"
                >
                  <v-icon icon="unarchive" />
                </v-button>
              </div>
            </div>
            <div v-if="!!resource.description" class="resource-description text-sm fw-light text-gray-500">
              <span v-html="resource.description" />
            </div>
            <span
              v-if="isHotel && resource.resourceType"
              class="room-type-badge"
            >
              {{ formatRoomType(resource.resourceType) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <the-supplier-resources-create-modal
      v-if="modalToShow === 'the-supplier-resources-create-modal'"
      :supplier="supplier"
      @closed="closeModal"
      @reload-supplier="reloadSupplier"
    />

    <the-supplier-resource-details-modal
      v-if="modalToShow === 'the-supplier-resource-details-modal'"
      :resource-id="currentResource"
      @closed="
        closeModal();
        currentResource = null;
      "
      @reload-supplier="reloadSupplier"
    />

    <the-supplier-details-rates-off-canvas
      v-if="offCanvasToShow === 'the-supplier-details-rates-off-canvas'"
      :supplier-id="supplierLocal.id"
      @closed="closeOffCanvas"
      @reload-supplier="reloadSupplier"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Vuedraggable from 'vuedraggable';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import useModalUtils from '@/helpers/ModalUtils';
import Supplier from '@/api/objects/Supplier';
import TheSupplierResourcesCreateModal from '@/components/supplier/resource/TheSupplierResourcesCreateModal.vue';
import SupplierResource from '@/api/objects/SupplierResource';
import TheSupplierResourceDetailsModal from '@/components/supplier/resource/TheSupplierResourceDetailsModal.vue';
import api from '@/api';
import useOffCanvasUtils from '@/helpers/OffCanvasUtils';
import { formatRoomType } from '@/composables/roomType';
import TheSupplierDetailsRatesOffCanvas from '@/components/supplier/parts/TheSupplierDetailsRatesOffCanvas.vue';

export default defineComponent({
  name: 'TheSupplierDetailsResources',
  components: {
    TheSupplierDetailsRatesOffCanvas,
    TheSupplierResourceDetailsModal,
    TheSupplierResourcesCreateModal,
    VIcon,
    VButton,
    draggable: Vuedraggable,
  },
  emits: ['closed', 'reloadSupplier'],
  props: {
    supplier: {
      type: Supplier,
      required: true,
    },
  },
  setup() {
    return {
      ...useModalUtils(),
      ...useOffCanvasUtils(),
      formatRoomType,
    };
  },
  data() {
    return {
      currentResource: null as null | number,
      supplierLocal: {} as Supplier,
      showHidden: false as boolean,
      visibleResources: [] as SupplierResource[],
      hiddenResources: [] as SupplierResource[],
    };
  },
  watch: {
    supplier(value) {
      if (!!value && !!value.id) {
        this.supplierLocal = value;
        this.splitResources();
      }
    },
  },
  created() {
    this.supplierLocal = this.supplier;
    this.splitResources();
  },
  computed: {
    isHotel(): boolean {
      return this.supplierLocal.type === 'hotel';
    },
  },
  methods: {
    async onChanged(event: any) {
      // Handle the change event when the order changesmessage
      const sortableId = event.moved.element.id;
      const newPosition = { position: event.moved.newIndex + 1 };
      const response = await api.supplierResourcePrice.sortable(sortableId, newPosition);

      this.$toast.success(response.message);
    },
    showSupplierResourceDetail(supplierResourceId: number) {
      this.currentResource = supplierResourceId;

      if (this.modalToShow !== 'the-supplier-resource-details-modal') {
        this.openModal('the-supplier-resource-details-modal');
      }
    },
    isHidden(resource: any): boolean {
      return resource.isHidden || resource.is_hidden;
    },
    splitResources() {
      this.visibleResources = (this.supplierLocal.resources || []).filter(
        (resource: SupplierResource) => !this.isHidden(resource),
      );
      this.hiddenResources = (this.supplierLocal.resources || []).filter(
        (resource: SupplierResource) => this.isHidden(resource),
      );
    },
    async hideSupplierResource(supplierResource: SupplierResource) {
      try {
        await api.supplierResource.hide(supplierResource.id);

        this.$toast.success(this.$t('general.shared.savedChanges'));

        this.reloadSupplier();
      } catch (e: any) {
        console.error(e.response?.data);

        this.$toast.error(e.response?.data?.message ?? this.$t('general.shared.error'));
      }
    },
    async unhideSupplierResource(supplierResource: SupplierResource) {
      try {
        await api.supplierResource.unhide(supplierResource.id);

        this.$toast.success(this.$t('general.shared.savedChanges'));

        this.reloadSupplier();
      } catch (e: any) {
        console.error(e.response?.data);

        this.$toast.error(e.response?.data?.message ?? this.$t('general.shared.error'));
      }
    },
    async deleteSupplierResource(supplierResource: SupplierResource) {
      await this.$modal.delete({
        title: this.$t('supplier.resource.archiveTitle'),
        text: this.$t('supplier.resource.archiveText', {
          resource: supplierResource.name,
          supplier: this.supplierLocal.name,
        }),
        deleteButtonText: this.$t('general.button.remove'),
        deleteButtonLoadingText: this.$t('general.button.removing'),
        rightButtonClasses: 'btn-tertiary',
        deleteButtonCallback: () => this.doDeleteSupplierResource(supplierResource.id),
      });
    },
    async doDeleteSupplierResource(supplierResourceId: string | number) {
      try {
        await api.supplierResource.archive(supplierResourceId);

        this.$toast.success(this.$t('general.shared.savedChanges'));

        this.reloadSupplier();
      } catch (e: any) {
        console.error(e.response?.data);

        this.$toast.error(e.response?.data?.message ?? this.$t('general.shared.error'));
      }
    },
    reloadSupplier() {
      this.$emit('reloadSupplier');
    },
    toggleHidden() {
      this.showHidden = !this.showHidden;
    },
  },
});
</script>

<style lang="scss">
.resource-description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  p {
    margin-bottom: 0;
  }
}

.room-type-badge {
  display: inline-block;
  color: #e61275;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 2px;
  padding: 0;
}
</style>
